import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { RegistrationRewardType } from "@redotech/redo-model/extended-warranty";
import { Payer } from "@redotech/redo-model/return-flow";
import {
  PurchaseChannel,
  RepairFeeCollectionStrategy,
  RetailAction,
} from "@redotech/redo-model/warranties";
import { Card } from "@redotech/redo-web/card";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { groupInput, InputProvider } from "@redotech/ui/form";
import { memo, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { updateExtendedWarrantySettings } from "../../client/warranties";
import { LoadTeamContext, TeamContext } from "../team";
import { CopyLatestFlowSettings } from "./shared/copy-latest-flow";
import { ExperienceDesignCard } from "./warranties/experience-design";
import {
  ExtendedWarranties,
  extendedWarrantyFormValueToTeam,
  extendedWarrantySettingsDefault,
  extendedWarrantySettingsForm,
  extendedWarrantyTeamToFormValue,
} from "./warranties/extended-warranties";
import {
  Warranties,
  warrantiesForm,
  warrantiesDefault as warrantiesGeneralDefault,
} from "./warranties/general";
import {
  NonShopifyClaimsRecycling,
  nonShopifyClaimsRecyclingDefault,
  nonShopifyClaimsRecyclingForm,
} from "./warranties/non-shopify-claims-recycling";

export const WarrantiesPage = memo(function WarrantiesPage() {
  const params = useParams();
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const handleSave = useHandler(() => {
    loadTeam();
  });

  const value = useMemo(() => {
    if (!team) {
      return;
    }

    const extendedWarrantyFormValue = extendedWarrantyTeamToFormValue(team);

    return {
      warranties: {
        enabled: team.settings.warranties?.enabled ?? false,
        warrantyRegistrationCollection:
          team.settings.warranties?.warrantyRegistrationCollection,
        purchaseChannelRegistrationCollection:
          team.settings.warranties?.purchaseChannelRegistrationCollection ?? [],
        labelCoverageEnabled:
          team.settings.warranties?.labelCoverageEnabled ?? false,
        portalFourRegistration:
          team.settings.warranties?.portalFourRegistration ?? true,
        redirectPostRegistration:
          team.settings.warranties?.redirectPostRegistration ?? "",
        customersCanStartClaims:
          team.settings.warranties?.enabled &&
          team.settings.warranties?.customersCanStartClaims,
        sendDefaultEmail: team.settings.warranties?.sendDefaultEmail ?? true,
        triggerMarketingFlow:
          team.settings.warranties?.triggerMarketingFlow ?? false,
        registrationRewardType:
          team.settings.warranties?.registrationRewardType ??
          RegistrationRewardType.DISABLED,
        registrationCustomerTags:
          team.settings.warranties?.registrationCustomerTags ?? [],
        repairFeeCollectionStrategy:
          team.settings.warranties?.repairFeeCollectionStrategy ??
          RepairFeeCollectionStrategy.DEFERRED,
        registrationEnabled:
          team.settings.warranties?.enabled &&
          team.settings.warranties?.registrationEnabled,
        redirectRegistrationToRetailActions:
          team.settings.warranties?.redirectRegistrationToRetailActions ??
          false,
        useWTYPrefixForRetail:
          team.settings.warranties?.useWTYPrefixForRetail ?? false,
        hideItemPricing: team.settings.warranties?.hideItemPricing ?? false,
        showOtherOption: team.settings.warranties?.showOtherOption ?? true,
        cantFindItemButtonEnabled:
          team.settings.warranties?.cantFindItemButtonEnabled ?? false,
        registrationRewardDiscountId:
          team.settings.warranties?.registrationRewardDiscountId,
        repairFeeEnabled: team.settings.warranties?.repairFeeEnabled ?? false,
        defaultRepairMaxPrice: team.settings.warranties?.defaultRepairMaxPrice
          ? String(team.settings.warranties.defaultRepairMaxPrice)
          : "1",
        defaultRepairMinPrice: team.settings.warranties?.defaultRepairMinPrice
          ? String(team.settings.warranties.defaultRepairMinPrice)
          : "1",
        repairPricingCustomCollections:
          team.settings.warranties?.repairPricingRuleSet?.map(
            (rule) => rule.collectionHandle,
          ) || [],
        repairPricingRuleSet:
          team.settings.warranties?.repairPricingRuleSet?.map((rule) => ({
            minPrice: String(rule.minPrice),
            maxPrice: String(rule.maxPrice),
            collectionHandle: rule.collectionHandle,
            collectionTitle: rule.collectionTitle,
          })) || [],
        eligibleUpsellTypes: team.settings.warranties?.eligibleUpsellTypes || [
          PurchaseChannel.RETAIL,
        ],
        enabledRetailActions: team.settings.warranties
          ?.enabledRetailActions || [RetailAction.REGISTER_PRODUCT],
        purchaseChannels:
          team.settings.warranties?.purchaseChannels &&
          team.settings.warranties?.purchaseChannels.length > 0
            ? team.settings.warranties.purchaseChannels
            : [
                {
                  displayName: "Shopify store",
                  purchaseChannel: PurchaseChannel.ONLINE,
                },
              ],
      },
      extendedWarranties: extendedWarrantyFormValue,
      nonShopifyClaimsRecycling: {
        enableRecycling:
          ((team.settings.recycling?.enabled ??
            team.settings.convertRecyclingProducts) &&
            team.settings.enableNonShopifyClaims) ??
          false,
        nonShopifyClaimsCollection:
          team.settings.recycling?.recyclableProductsCollection ??
          team.settings.nonShopifyClaimsCollection,
        nonShopifyClaimsDisableEditQuantity:
          team.settings.nonShopifyClaimsDisableEditQuantity ?? false,
        recyclingProductId:
          team.settings.recycling?.productId ??
          team.settings.recyclingProductId ??
          "",
        labelPayer: team.settings.recycling?.labelPayer ?? Payer.MERCHANT,
        chargeMerchantPerRecycleRequest:
          team.settings.recycling?.chargeMerchantPerRecycleRequest ?? false,
        perRecycleRequestFee: String(
          team.settings.recycling?.perRecycleRequestFee ?? 3,
        ),
        minRecyclingProductQuantity: String(
          team.settings.recycling?.minRecyclingProductQuantity ?? 5,
        ),
      },
    };
  }, [team]);

  return value && team ? (
    <WarrantiesForm
      id={teamId}
      onSave={handleSave}
      teamName={team.name}
      value={value}
    />
  ) : null;
});

const warrantiesPageForm = groupInput({
  warranties: warrantiesForm,
  extendedWarranties: extendedWarrantySettingsForm,
  nonShopifyClaimsRecycling: nonShopifyClaimsRecyclingForm,
});

type WarrantiesValue = InputProvider.Value<typeof warrantiesPageForm>;

const warrantiesDefault: WarrantiesValue = {
  warranties: warrantiesGeneralDefault,
  extendedWarranties: extendedWarrantySettingsDefault,
  nonShopifyClaimsRecycling: nonShopifyClaimsRecyclingDefault,
};

const WarrantiesForm = memo(function WarrantiesForm({
  id,
  onSave,
  teamName,
  value,
}: {
  id: string;
  onSave: (value: WarrantiesValue) => void;
  teamName: string;
  value: WarrantiesValue | undefined;
}) {
  const client = useRequiredContext(RedoAdminClientContext);

  const input = useInput(warrantiesPageForm, value || warrantiesDefault);

  const { warranties, extendedWarranties, nonShopifyClaimsRecycling } =
    input.inputs;

  const save: PageFormSave = useHandler(async (signal) => {
    const team = await getTeam(client, { teamId: id, signal });
    const value = input.value;

    team.settings.extendedWarranties = extendedWarrantyFormValueToTeam(
      value.extendedWarranties,
    );
    team.settings.warranties = {
      enabled: value.warranties.enabled,
      labelCoverageEnabled: value.warranties.labelCoverageEnabled,
      portalFourRegistration: value.warranties.portalFourRegistration,
      redirectPostRegistration:
        value.warranties.redirectPostRegistration || undefined,
      warrantyRegistrationCollection:
        value.warranties.warrantyRegistrationCollection,
      purchaseChannelRegistrationCollection:
        value.warranties.purchaseChannelRegistrationCollection,
      sendDefaultEmail: value.warranties.sendDefaultEmail,
      triggerMarketingFlow: value.warranties.triggerMarketingFlow,
      customersCanStartClaims:
        value.warranties.enabled && value.warranties.customersCanStartClaims,
      registrationEnabled:
        value.warranties.enabled && value.warranties.registrationEnabled,
      redirectRegistrationToRetailActions:
        value.warranties.redirectRegistrationToRetailActions,
      useWTYPrefixForRetail: value.warranties.useWTYPrefixForRetail,
      registrationRewardType: value.warranties.registrationRewardType,
      registrationCustomerTags: value.warranties.registrationCustomerTags,
      registrationRewardDiscountId:
        value.warranties.registrationRewardDiscountId,
      hideItemPricing: value.warranties.hideItemPricing,
      showOtherOption: value.warranties.showOtherOption,
      cantFindItemButtonEnabled: value.warranties.cantFindItemButtonEnabled,
      repairFeeEnabled: value.warranties.repairFeeEnabled,
      repairFeeCollectionStrategy: value.warranties.repairFeeCollectionStrategy,
      defaultRepairMaxPrice: Number(value.warranties.defaultRepairMaxPrice),
      defaultRepairMinPrice: Number(value.warranties.defaultRepairMinPrice),
      repairPricingRuleSet: value.warranties.repairPricingRuleSet.map(
        (rule) => ({
          minPrice: Number(rule.minPrice),
          maxPrice: Number(rule.maxPrice),
          collectionHandle: rule.collectionHandle,
          collectionTitle: rule.collectionTitle,
        }),
      ),
      purchaseChannels: value.warranties.purchaseChannels,
      eligibleUpsellTypes: value.warranties.eligibleUpsellTypes,
      enabledRetailActions: value.warranties.enabledRetailActions,
    };

    // Save non-Shopify claims & recycling settings to team.settings (not nested under warranties)
    team.settings.enableNonShopifyClaims =
      value.nonShopifyClaimsRecycling.enableRecycling;
    team.settings.nonShopifyClaimsCollection =
      value.nonShopifyClaimsRecycling.nonShopifyClaimsCollection;
    team.settings.nonShopifyClaimsDisableEditQuantity =
      value.nonShopifyClaimsRecycling.nonShopifyClaimsDisableEditQuantity;
    team.settings.convertRecyclingProducts =
      value.nonShopifyClaimsRecycling.enableRecycling;
    team.settings.recyclingProductId =
      value.nonShopifyClaimsRecycling.recyclingProductId || undefined;

    team.settings.recycling = {
      enabled: value.nonShopifyClaimsRecycling.enableRecycling,
      recyclableProductsCollection:
        value.nonShopifyClaimsRecycling.nonShopifyClaimsCollection,
      productId: value.nonShopifyClaimsRecycling.recyclingProductId,
      labelPayer: value.nonShopifyClaimsRecycling.labelPayer,
      chargeMerchantPerRecycleRequest:
        value.nonShopifyClaimsRecycling.chargeMerchantPerRecycleRequest,
      perRecycleRequestFee: Number(
        value.nonShopifyClaimsRecycling.perRecycleRequestFee,
      ),
      minRecyclingProductQuantity: Number(
        value.nonShopifyClaimsRecycling.minRecyclingProductQuantity,
      ),
    };

    // TODO: Add sideImage and logo for extended warranties to admin dashboard
    if (input.inputs.extendedWarranties.changed) {
      await updateExtendedWarrantySettings(client, {
        teamId: id,
        extendedWarrantySettings: team.settings.extendedWarranties,
        // TODO: Add back in as dynamic resource
        sideImage:
          value.extendedWarranties.experienceDesign.sideImage?.toString(),
        logo: value.extendedWarranties.experienceDesign.logo?.toString(),
        signal,
      });
    }

    await updateTeam(client, { teamId: id, team, signal });

    onSave(value);
  });

  return (
    <PageForm initial={value || warrantiesDefault} input={input} save={save}>
      <div className={gridCss.grid}>
        <div className={gridCss.span6L}>
          <Warranties input={warranties} />
        </div>
        <div className={gridCss.span6L}>
          <ExtendedWarranties input={extendedWarranties} />
        </div>
        <div className={gridCss.span6L}>
          <NonShopifyClaimsRecycling input={nonShopifyClaimsRecycling} />
        </div>
        <div className={gridCss.span12}>
          <ExperienceDesignCard
            extendedWarrantySettings={extendedWarranties.value}
            input={extendedWarranties.inputs.experienceDesign}
          />
        </div>
        <div className={gridCss.span6L}>
          <Card title="Copy warranty flow">
            <CopyLatestFlowSettings
              flowType="warranty"
              sourceTeamId={id}
              sourceTeamName={teamName}
            />
          </Card>
        </div>
      </div>
    </PageForm>
  );
});
